import { useReducedMotion } from 'framer-motion'
import { mineral } from '../../theme'

/**
 * El sistema en movimiento, como fondo del login: camiones que salen de las
 * bocaminas, pasan por garita y llegan a planta. Cada llegada hace latir la
 * planta y sube un "documento sellado" (el expediente del lote creciendo).
 *
 * SVG + SMIL nativo (animateMotion): miles de frames sin React de por medio.
 * Las llegadas están sincronizadas por cálculo, no por eventos: cada pulso de
 * la planta repite con el mismo período que el camión de su ruta y arranca
 * justo cuando éste completa su primer recorrido.
 */

interface Route {
  id: string
  d: string
  mine: [number, number]
  dur: number
  offsets: number[]
}

/**
 * ZONA SEGURA: el SVG usa `slice`, así que en pantallas anchas se recorta arriba y
 * abajo (en 1920×860 sólo se ve y≈130–770 del viewBox). Todo lo importante —planta,
 * bocaminas, garitas— vive en y∈[180, 700] para que nunca quede bajo el pie ni
 * fuera de la vista.
 */
const PLANT: [number, number] = [760, 660]

const ROUTES: Route[] = [
  { id: 'r1', mine: [120, 200], d: 'M120 200 C 300 220, 380 420, 520 470 S 700 600, 760 660', dur: 14, offsets: [0, 7] },
  { id: 'r2', mine: [600, 180], d: 'M600 180 C 660 280, 620 380, 700 450 S 790 600, 760 660', dur: 11, offsets: [2.5] },
  { id: 'r3', mine: [1290, 210], d: 'M1290 210 C 1180 230, 1100 170, 990 220 S 840 380, 840 500 S 800 620, 760 660', dur: 17, offsets: [5, 13.5] },
  { id: 'r4', mine: [90, 680], d: 'M90 680 C 250 720, 450 600, 600 680 S 720 690, 760 660', dur: 12, offsets: [1.2] },
  { id: 'r5', mine: [1310, 690], d: 'M1310 690 C 1220 650, 1120 720, 980 690 S 820 680, 760 660', dur: 13, offsets: [8.5] },
]

/** Garitas: puntos de control sobre las rutas. */
const CHECKPOINTS: Array<[number, number]> = [[705, 615], [840, 500], [600, 680]]

/**
 * Camión en vista CENITAL (desde arriba): es la vista de un mapa y, al girar con la
 * ruta (`rotate="auto"`), nunca queda "de cabeza" como pasaba con el de perfil.
 * Mira hacia +x: tolva con mineral atrás, cabina con parabrisas adelante, haz de faros.
 */
function Truck({ color }: { color: string }) {
  return (
    <g>
      {/* Haz de faros: marca el sentido de la marcha */}
      <path d="M14 -4 L30 -9 L30 9 L14 4 Z" fill="url(#rf-beam)" />
      {/* Ruedas asomando */}
      {[-12, -6, 8].map(x => (
        <g key={x}>
          <rect x={x} y={-8.2} width={3.6} height={2} rx={0.8} fill="#141414" />
          <rect x={x} y={6.2} width={3.6} height={2} rx={0.8} fill="#141414" />
        </g>
      ))}
      {/* Tolva con carga de mineral */}
      <rect x={-16} y={-6.5} width={20} height={13} rx={2} fill={color} />
      <rect x={-14.2} y={-4.7} width={16.4} height={9.4} rx={1.4} fill="rgba(0,0,0,0.22)" />
      <circle cx={-10} cy={-1.2} r={2.6} fill={mineral.ocreDark} />
      <circle cx={-5.5} cy={1.6} r={2.9} fill={mineral.cobreDeep} />
      <circle cx={-1.2} cy={-1.4} r={2.3} fill={mineral.ocreDark} />
      <circle cx={-6.8} cy={-2.6} r={1.2} fill={mineral.ocreLight} opacity={0.8} />
      {/* Cabina */}
      <rect x={5.5} y={-6} width={8.5} height={12} rx={2.6} fill={color} />
      <rect x={5.5} y={-6} width={8.5} height={12} rx={2.6} fill="rgba(255,255,255,0.18)" />
      {/* Parabrisas */}
      <rect x={10.6} y={-4.6} width={2.2} height={9.2} rx={1} fill="#0f2f30" />
      <rect x={10.9} y={-3.8} width={0.8} height={3} rx={0.4} fill="rgba(255,255,255,0.55)" />
      {/* Espejos */}
      <rect x={9} y={-7.6} width={1.6} height={1.6} rx={0.4} fill={color} />
      <rect x={9} y={6} width={1.6} height={1.6} rx={0.4} fill={color} />
    </g>
  )
}

export default function RouteFlow() {
  const reduce = useReducedMotion()

  return (
    <svg viewBox="0 0 1440 900" preserveAspectRatio="xMidYMid slice" aria-hidden
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      <defs>
        <radialGradient id="rf-plant" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={mineral.bosque300} stopOpacity="0.55" />
          <stop offset="100%" stopColor={mineral.bosque300} stopOpacity="0" />
        </radialGradient>
        <linearGradient id="rf-beam" x1="0" x2="1" y1="0" y2="0">
          <stop offset="0%" stopColor="#fff6d8" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#fff6d8" stopOpacity="0" />
        </linearGradient>
        <filter id="rf-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="1.6" result="b" />
          <feMerge><feMergeNode in="b" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
        {ROUTES.map(r => <path key={r.id} id={`rf-${r.id}`} d={r.d} />)}
      </defs>

      {/* Rutas: base tenue + flujo direccional */}
      {ROUTES.map((r, i) => (
        <g key={r.id}>
          <path d={r.d} fill="none" stroke="rgba(240,185,143,0.14)" strokeWidth={6} strokeLinecap="round" />
          <path d={r.d} fill="none" stroke={i % 2 ? 'rgba(147,207,201,0.5)' : 'rgba(240,185,143,0.55)'} strokeWidth={1.6}
            strokeDasharray="3 11" strokeLinecap="round">
            {!reduce && <animate attributeName="stroke-dashoffset" from="0" to="-140" dur="3.5s" repeatCount="indefinite" />}
          </path>
        </g>
      ))}

      {/* Bocaminas: despachan (pulso) cuando sale su camión */}
      {ROUTES.map(r => (
        <g key={`m-${r.id}`} transform={`translate(${r.mine[0]} ${r.mine[1]})`}>
          {!reduce && r.offsets.map((o, k) => (
            <circle key={k} r={6} fill="none" stroke={mineral.ocre} strokeWidth={1.5}>
              <animate attributeName="r" values="6;26;26" keyTimes="0;0.15;1" dur={`${r.dur}s`} begin={`${o}s`} repeatCount="indefinite" />
              <animate attributeName="opacity" values="0.9;0;0" keyTimes="0;0.15;1" dur={`${r.dur}s`} begin={`${o}s`} repeatCount="indefinite" />
            </circle>
          ))}
          <rect x={-6} y={-6} width={12} height={12} transform="rotate(45)" fill={mineral.ocre} stroke="rgba(255,255,255,0.6)" strokeWidth={1} />
          <text x={12} y={-9} fill="rgba(255,255,255,0.42)" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} letterSpacing="1.5">BOCAMINA</text>
        </g>
      ))}

      {/* Garitas */}
      {CHECKPOINTS.map(([x, y], i) => (
        <g key={`g-${i}`} transform={`translate(${x} ${y})`}>
          <rect x={-5} y={-5} width={10} height={10} rx={2} fill="none" stroke={mineral.oro} strokeWidth={1.5} opacity={0.7}>
            {!reduce && <animate attributeName="opacity" values="0.35;0.9;0.35" dur={`${3 + i}s`} repeatCount="indefinite" />}
          </rect>
          <text x={10} y={3} fill="rgba(255,255,255,0.32)" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }} letterSpacing="1.5">GARITA</text>
        </g>
      ))}

      {/* Planta */}
      <g transform={`translate(${PLANT[0]} ${PLANT[1]})`}>
        <circle r={70} fill="url(#rf-plant)" />
        {!reduce && ROUTES.flatMap(r => r.offsets.map((o, k) => (
          <circle key={`${r.id}-${k}`} r={16} fill="none" stroke={mineral.bosque300} strokeWidth={2}>
            <animate attributeName="r" values="16;64;64" keyTimes="0;0.14;1" dur={`${r.dur}s`} begin={`${o + r.dur}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0.85;0;0" keyTimes="0;0.14;1" dur={`${r.dur}s`} begin={`${o + r.dur}s`} repeatCount="indefinite" />
          </circle>
        )))}
        <circle r={15} fill={mineral.bosque600} stroke={mineral.bosque300} strokeWidth={2} />
        <path d="M-7 5 V-2 L-3 -5 V-1 L1 -4 V0 L5 -3 V5 Z" fill="#fff" />
        <text x={26} y={-4} fill="rgba(255,255,255,0.7)" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }} fontWeight={600} letterSpacing="2">PLANTA</text>
        <text x={26} y={12} fill="rgba(255,255,255,0.38)" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10 }} letterSpacing="1.2">RECEPCIÓN · EXPEDIENTE</text>

        {/* Documento sellado: sube en cada llegada */}
        {!reduce && ROUTES.flatMap(r => r.offsets.map((o, k) => (
          <g key={`doc-${r.id}-${k}`} opacity={0}>
            <animateTransform attributeName="transform" type="translate" values="-6 -18; -6 -78; -6 -78" keyTimes="0;0.22;1"
              dur={`${r.dur}s`} begin={`${o + r.dur}s`} repeatCount="indefinite" />
            <animate attributeName="opacity" values="0;1;0;0" keyTimes="0;0.04;0.22;1" dur={`${r.dur}s`} begin={`${o + r.dur}s`} repeatCount="indefinite" />
            <path d="M0 0 H9 L13 4 V16 H0 Z" fill={mineral.ocreSoft} />
            <path d="M3 9 L5.5 11.5 L10 6.5" fill="none" stroke={mineral.bosque600} strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" />
          </g>
        )))}
      </g>

      {/* Camiones */}
      {!reduce && ROUTES.flatMap((r, i) => r.offsets.map((o, k) => (
        <g key={`t-${r.id}-${k}`} opacity={0} filter="url(#rf-glow)">
          <animateMotion dur={`${r.dur}s`} begin={`${o}s`} repeatCount="indefinite" rotate="auto" keyPoints="0;1" keyTimes="0;1" calcMode="spline" keySplines="0.45 0 0.55 1">
            <mpath href={`#rf-${r.id}`} />
          </animateMotion>
          <animate attributeName="opacity" values="0;1;1;0" keyTimes="0;0.04;0.95;1" dur={`${r.dur}s`} begin={`${o}s`} repeatCount="indefinite" />
          <g transform="scale(1.8)"><Truck color={i % 2 ? mineral.bosque500 : mineral.ocre} /></g>
        </g>
      )))}
    </svg>
  )
}
